import React from 'react';

import CtaWithHint from 'components/CtaWithHint';
import LegalPageLayout from 'components/LegalPageLayout';
import LocalizedLink from 'components/LocalizedLink';
import TrustProof from 'components/TrustProof';
import { GUIDE_SLUGS, VERTICAL_SLUGS, type VerticalSlug } from 'content/marketingRoutes';
import { useI18n } from 'i18n/I18nContext';

const PAIN_KEYS = ['p1', 'p2', 'p3', 'p4'] as const;
const FIT_KEYS = ['f1', 'f2', 'f3', 'f4'] as const;

export interface VerticalPageProps {
  slug: VerticalSlug;
}

const VerticalPage = ({ slug }: VerticalPageProps) => {
  const { t } = useI18n();
  const prefix = `verticals.${slug}`;

  return (
    <LegalPageLayout
      title={t(`${prefix}.title`)}
      lede={t(`${prefix}.intro`)}
      variant="marketing"
      wide
    >
      <div className="gb-vertical-split">
        <section className="gb-vertical-panel" aria-labelledby="vertical-pains-heading">
          <h2 id="vertical-pains-heading" className="gb-vertical-panel__title">
            {t(`${prefix}.painsTitle`)}
          </h2>
          <ol className="gb-vertical-points">
            {PAIN_KEYS.map((key, index) => (
              <li key={key}>
                <span className="gb-vertical-points__n" aria-hidden>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>{t(`${prefix}.pains.${key}`)}</span>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="gb-vertical-panel gb-vertical-panel--fit"
          aria-labelledby="vertical-fits-heading"
        >
          <h2 id="vertical-fits-heading" className="gb-vertical-panel__title">
            {t(`${prefix}.fitsTitle`)}
          </h2>
          <ol className="gb-vertical-points">
            {FIT_KEYS.map((key, index) => (
              <li key={key}>
                <span className="gb-vertical-points__n" aria-hidden>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>{t(`${prefix}.fits.${key}`)}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <div className="gb-resource-cta gb-vertical-cta">
        <CtaWithHint
          href="/onboard"
          buttonLabel={t('verticals.cta')}
          hint={t('verticals.ctaHint')}
        />
        <TrustProof className="mt-25" />
      </div>

      <nav className="gb-vertical-more" aria-label={t('verticals.otherVerticals')}>
        <div className="gb-vertical-more__block">
          <h2 className="gb-vertical-more__title">{t('verticals.otherVerticals')}</h2>
          <ul className="gb-vertical-more__list">
            {VERTICAL_SLUGS.filter((item) => item !== slug).map((item) => (
              <li key={item}>
                <LocalizedLink className="gb-vertical-more__link" to={`/for/${item}`}>
                  {t(`verticals.${item}.label`)}
                </LocalizedLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="gb-vertical-more__block">
          <h2 className="gb-vertical-more__title">{t('guides.otherGuides')}</h2>
          <ul className="gb-vertical-more__list">
            {GUIDE_SLUGS.slice(0, 3).map((item) => (
              <li key={item}>
                <LocalizedLink className="gb-vertical-more__link" to={`/guides/${item}`}>
                  {t(`guides.${item}.title`)}
                </LocalizedLink>
              </li>
            ))}
          </ul>
          <p className="gb-vertical-more__meta">
            <LocalizedLink className="gb-vertical-more__link" to="/guides">
              {t('verticals.seeGuides')}
            </LocalizedLink>
            <span className="gb-vertical-more__dot" aria-hidden>
              ·
            </span>
            <LocalizedLink className="gb-vertical-more__link" to="/compare">
              {t('verticals.seeCompare')}
            </LocalizedLink>
          </p>
        </div>
      </nav>
    </LegalPageLayout>
  );
};

export default VerticalPage;



// WEBPACK FOOTER //
// ./src/pages/VerticalPage.tsx